import React, { JSX } from 'react'
import { Chip } from '@mui/material'
import {
    DateTimePicker,
    LocalizationProvider,
    DatePicker,
} from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import 'moment-timezone'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    BaseDisplayItemState,
    DisplayItemBase,
} from '../BaseDisplayItem'
import { DateTimeFieldShow } from './DateTimeFieldShow'
import { DateTimeFieldEdit } from './DateTimeFieldEdit'
import { DateTimeFieldSearch } from './DateTimeFieldSearch'
import { formatDateTimeValue, getDateTimeLocale } from './formatDateTimeValue'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface DateTimeFieldProps
    extends BaseDisplayItemProps<DateTimeValue, DateTimeItem> {}

export type DateTimeValue =
    | string
    | moment.Moment
    | Date
    | null
    | {
          custom: string
          type: string
          date1?: string | null
          date2?: string | null
      }

export interface DateTimeItem extends DisplayItemBase {
    settings?: {
        current_time_as_default?: boolean
        default_current_date?: boolean
        show_slider?: boolean
        deadline?: Array<{
            number: number
            type: string
            color: string
            when: string
        }> | null
        restriction?: string | null
        withtime?: boolean
        dossier_data?: string
        show_ago?: boolean
    }
}

export interface DateTimeFieldExtendedState
    extends BaseDisplayItemState<DateTimeValue> {
    show_slider: boolean
    deadline: Array<{
        number: number
        type: string
        color: string
        when: string
    }> | null
    searchType: string
    date1: string | null
    date2: string | null
    current_time_as_default: boolean
    restriction?: string | null | unknown
    withtime: boolean
}

class DateTimeField extends BaseDisplayItem<
    DateTimeValue,
    DateTimeItem,
    HTMLDivElement,
    DateTimeFieldExtendedState
> {
    constructor(props: DateTimeFieldProps) {
        super(props)
        this.kedo = props.kedo

        const currentDefault = this.getSetting<boolean>(
            'current_time_as_default'
        )

        this.state = {
            value:
                this.props.value ?? (currentDefault ? moment().format() : null),
            show_slider: this.props.item?.settings?.show_slider === true,
            deadline: this.props.item?.settings?.deadline ?? null,
            searchType:
                (this.props.mode === 'search' ||
                    this.props.mode === 'advanced-search') &&
                this.props.value &&
                (this.props.value as any).type
                    ? (this.props.value as any).type
                    : 'on',
            date1:
                (this.props.mode === 'search' ||
                    this.props.mode === 'advanced-search') &&
                this.props.value &&
                (this.props.value as any).date1
                    ? (this.props.value as any).date1
                    : null,
            date2:
                (this.props.mode === 'search' ||
                    this.props.mode === 'advanced-search') &&
                this.props.value &&
                (this.props.value as any).date2
                    ? (this.props.value as any).date2
                    : null,
            current_time_as_default: !!currentDefault,
            restriction:
                this.props.item?.def_dossier_def_field?.def_field?.settings
                    ?.restriction ?? null,
            withtime:
                !!this.props.item.def_dossier_def_field?.def_field?.settings
                    ?.withtime,
        }

        this.onChangeSearch = this.onChangeSearch.bind(this)
        this.onChangeDate = this.onChangeDate.bind(this)
        this.getFormattedDate = this.getFormattedDate.bind(this)
        this.isInvalid = this.isInvalid.bind(this)
    }

    onChangeSearch(searchType: string) {
        const searchValue = {
            custom: 'date',
            type: searchType,
            date1: this.state.date1,
            date2: this.state.date2,
        }

        this.setState({ searchType })

        if (
            this.props.item.settings &&
            this.props.item.settings.dossier_data &&
            (this.props.item.settings.dossier_data === 'created_at' ||
                this.props.item.settings.dossier_data === 'modified_at')
        ) {
            this.props.changeValue?.(
                this.props.item.settings.dossier_data,
                searchValue
            )
        } else {
            this.props.changeValue?.(this.props.item.id, searchValue)
        }
    }

    onChangeDate(
        moment: moment.Moment | null,
        name?: string,
        searchType?: string
    ) {
        if (this.props.changeValue && moment && (moment as any).length <= 0) {
            this.props.changeValue(this.props.item.id, null)
        }

        if (!moment || !moment.format) {
            this.setState({ value: null })

            this.props.changeValue?.(this.props.item.id, null)
            return
        }

        const searchValue = {
            custom: 'date',
            type: this.state.searchType,
            date1: this.state.date1,
            date2: this.state.date2,
        }

        if (moment) {
            if (name === 'date1') {
                searchValue.date1 = moment.format()
                this.setState({ date1: moment.format() })
            } else if (name === 'date2') {
                searchValue.date2 = moment.format()
                this.setState({ date2: moment.format() })
            } else {
                this.setState({ value: moment.format() })
            }
        } else if (searchType) {
            searchValue.type = searchType
            this.setState({ searchType: searchType })
        }

        if (
            this.props.item.settings &&
            this.props.item.settings.dossier_data &&
            (this.props.item.settings.dossier_data === 'created_at' ||
                this.props.item.settings.dossier_data === 'modified_at')
        ) {
            this.props.changeValue?.(
                this.props.item.settings.dossier_data,
                searchValue
            )
        } else {
            this.props.changeValue?.(this.props.item.id, searchValue)
        }
    }

    getFormattedDate(value: string): string | null {
        return formatDateTimeValue(
            value,
            this.getCurrentLocale(),
            this.state.withtime
        )
    }

    getSearchTypeTitle(searchType: string): string {
        const kedo = (this.props as DateTimeFieldProps).kedo
        if (!searchType || searchType === 'on') {
            return kedo.t('time_on')
        }

        if (searchType === 'after') {
            return kedo.t('time_after')
        }

        if (searchType === 'before') {
            return kedo.t('time_before')
        }

        if (searchType === 'between') {
            return kedo.t('time_from_till')
        }

        return searchType.charAt(0).toUpperCase() + searchType.slice(1)
    }

    getCurrentLocale(): string {
        return getDateTimeLocale((this.props as DateTimeFieldProps).kedo)
    }

    getSearchDateTime(name: string, value: string | null) {
        return (
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={this.getCurrentLocale()}
            >
                {this.state.withtime ? (
                    <DateTimePicker
                        value={value ? moment(value) : null}
                        onChange={(newValue) => {
                            this.onChangeDate(newValue, name)
                        }}
                        slotProps={{
                            textField: { variant: 'outlined', size: 'small' },
                        }}
                    />
                ) : (
                    <DatePicker
                        value={value ? moment(value) : null}
                        onChange={(newValue) => {
                            this.onChangeDate(newValue, name)
                        }}
                        slotProps={{
                            textField: { variant: 'outlined', size: 'small' },
                        }}
                    />
                )}
            </LocalizationProvider>
        )
    }

    getColor(
        backgroundColor: string,
        text: string,
        fontSize = '90%'
    ): JSX.Element {
        return (
            <Chip
                label={text}
                size="small"
                sx={{
                    backgroundColor: backgroundColor,
                    color: '#fff',
                    fontSize: fontSize,
                    borderRadius: '16px',
                    height: 'auto',
                    px: 1.5,
                }}
            />
        )
    }

    renderShow(): React.ReactNode {
        return (
            <DateTimeFieldShow
                kedo={this.kedo}
                value={this.state.value as string}
                settings={this.props.item.settings}
                getFormattedDate={this.getFormattedDate}
                deadline={this.state.deadline}
                getColor={this.getColor}
            />
        )
    }

    renderEdit(): React.ReactNode {
        return (
            <DateTimeFieldEdit
                kedo={this.kedo}
                state={this.state}
                errors={this.props.errors}
                value={this.state.value}
                onChangeDate={this.onChangeDate}
                isInvalid={this.isInvalid}
                getErrorText={this.getErrorText}
                getCurrentLocale={this.getCurrentLocale}
            />
        )
    }

    renderSearch(): React.ReactNode {
        return (
            <DateTimeFieldSearch
                kedo={this.kedo}
                date1={this.state.date1}
                date2={this.state.date2}
                withtime={this.state.withtime}
                searchType={this.state.searchType}
                show_slider={this.state.show_slider}
                onChangeSearch={this.onChangeSearch}
                onChangeDate={this.onChangeDate}
                getSearchTypeTitle={this.getSearchTypeTitle}
                getSearchDateTime={this.getSearchDateTime}
                isInvalid={this.isInvalid}
                getErrorText={this.getErrorText}
            />
        )
    }

    shouldComponentUpdate(
        nextProps: DateTimeFieldProps,
        nextState: DateTimeFieldExtendedState,
        nextContext: any
    ) {
        if (
            this.state.searchType !== nextState.searchType ||
            this.state.date1 !== nextState.date1 ||
            this.state.date2 !== nextState.date2 ||
            this.state.value !== nextState.value
        ) {
            return true
        }
        if (
            nextProps.mode !== this.props.mode ||
            nextProps.errors !== this.props.errors
        ) {
            return true
        }

        return false
    }
}

export default DateTimeField
